import { fetchSectionText, hasCachedSection } from './utils-section-cache.js';

class ProductGroupSelect extends HTMLElement {
  abortController;

  /**
   * Connected callback.
   */
  connectedCallback() {
    this.initOptions();
  }

  /**
   * Disconnected callback.
   */
  disconnectedCallback() {
    this.abortController = null;
  }

  /**
   * Initialize the options.
   */
  initOptions() {
    this.sectionId = this.dataset.sectionId;
    this.currentProductUrl = this.dataset.currentProductUrl;

    // On click on grouped product
    this.clickHandler = (event) => {
      const target = event.currentTarget;
      if (target && !this.abortController) {
        event.preventDefault();
        this.onGroupChange({ target });
      }
    };
    // Prefetch on hover/focus to warm the cache before the click.
    this.prefetchHandler = (event) => {
      const target = event.currentTarget;
      if (target) this.prefetchGroup(target);
    };
    this.querySelectorAll('[data-group-product-url]').forEach((option) => {
      option.addEventListener('click', this.clickHandler);
      option.addEventListener('pointerenter', this.prefetchHandler);
      option.addEventListener('focus', this.prefetchHandler);
    });

    // On keydown on grouped product
    this.keydownHandler = (event) => {
      if (event.key !== 'Enter') return;
      const target = event.target.closest('[data-group-product-url]');
      if (target && !this.abortController) {
        event.preventDefault();
        this.onGroupChange({ target });
      }
    };
    this.addEventListener('keydown', this.keydownHandler);
  }

  /**
   * Get the new variant ID.
   * The matching variant is resolved server-side and embedded on the option,
   * so the section stays in sync with the current selection on every re-render.
   * @param {HTMLElement} target - The target element.
   * @returns {String} The variant ID.
   */
  getVariantId(target) {
    return target.dataset.groupVariantId;
  }

  /**
   * Handle on group product change.
   * @param {Event} event - The event object.
   */
  async onGroupChange({ target }) {
    // Return if this target is already selected
    if (target.classList.contains('group-option-selected')) return;

    // Create a new abort controller
    if (this.abortController) return;
    this.abortController = new AbortController();

    // Show the loader
    document.querySelector('[data-product-options-loader]').classList.add('active');

    // Get the new variant ID
    const variantId = this.getVariantId(target);

    // Immediately uncheck all other checkboxes within the same fieldset to improve perceived speed.
    this.updateFieldset(target);

    // Fetch the new product
    try {
      const params = this.createParams(variantId);
      const fetchUrl = `${target.dataset.groupProductUrl}?${params}`;

      // Get the new product HTML from the cache or fetch it from the server
      const text = await fetchSectionText(fetchUrl, this.abortController.signal);
      const parsedHTML = new DOMParser().parseFromString(text, 'text/html');

      // Update the URL
      this.updateURL(target.dataset.groupProductUrl, params);

      // Publish the new product
      publish(`product:changed:${this.sectionId}`, {
        variantId,
        html: parsedHTML,
      });
    } catch (error) {
      if (error.name === 'AbortError')
        return console.error('Variant change request was aborted due to a new variant selection.');

      console.error(error.message);
      window.toast.error(window.toast_translations.product.variant_error);
    } finally {
      this.abortController = null;
      document.querySelector(`#${target.id}`).focus();
      document.querySelector('[data-product-options-loader]').classList.remove('active');
    }
  }

  /**
   * Warm the cache for a grouped product before it is selected.
   * @param {HTMLElement} target - The grouped product option element.
   */
  prefetchGroup(target) {
    if (!target || target.classList.contains('group-option-selected')) return;

    const variantId = this.getVariantId(target);
    if (!variantId) return;

    const fetchUrl = `${target.dataset.groupProductUrl}?${this.createParams(variantId)}`;
    if (hasCachedSection(fetchUrl)) return;

    // Warm the cache quietly; log errors (a real click will surface them to the user).
    fetchSectionText(fetchUrl).catch((error) => console.error('Group prefetch failed:', error));
  }

  /**
   * Update URL
   * @param {String} url - URL value
   * @param {String} params - The params as a string
   */
  updateURL(url, params) {
    // Return if no URL or params, or if the URL is the same as the current product URL
    if (!url) return;
    if (!params) return;
    if (url === this.currentProductUrl) return;

    // Remove section_id from params
    const newParams = new URLSearchParams(params);
    newParams.delete('section_id');

    // Replace the current history state with the new search params, without reloading the page
    try {
      history.replaceState(null, '', `${url}?${newParams}`);
    } catch (error) {
      console.error('Error updating URL', error);
    }
  }

  /**
   * Create the params.
   * @param {String} variantId - The variant ID.
   * @returns {String} The params.
   */
  createParams(variantId) {
    const params = new URLSearchParams(window.location.search);
    params.set('section_id', this.sectionId);
    params.set('variant', variantId);
    return params.toString();
  }

  /**
   * Update the fieldset.
   * @param {HTMLElement} target - The target element.
   */
  updateFieldset(target) {
    if (!target) return;
    this.querySelectorAll('[data-group-product-url]').forEach((option) => {
      option.classList.toggle('group-option-selected', option === target);
    });
  }
}

customElements.define('product-group-select', ProductGroupSelect);
